import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  FlightAction,
  type FlightActionState,
} from "@b2bportal/air-booking-api";
import {
  ALL_FLIGHT_STATES,
  flightsPostBookingApi,
} from "@b2bportal/core-flights";
import type { AppDispatch } from "@b2bportal/core-types";
import { logger } from "@hopper-b2b/datadog";
import { useExperiment, useExperiments } from "@hopper-b2b/experiments";
import { resolveFlightActionRoute } from "@hopper-b2b/trips/src/utils/flightActionCapabilities";
import dayjs from "dayjs";

import { FEATURE_FLAGS } from "../App";
import { buildCommerceFlightActionUrl } from "../utils/commercePlatformRedirect";
import {
  buildLegacyFlightActionPath,
  type FlightActionEntry,
  type FlightExerciseAction,
} from "../utils/postLoginRedirect";
import { PATH_TRIPS } from "../utils/urlPaths";
import type { FlightTripDetailsLocationState } from "./useCommerceFlightTripDetailsRedirect";

/**
 * Upper bound on how long the capability read may take. The My Trips
 * list fetch can HANG, or never start if the session never flips
 * `isLoggedIn`; without this the user would sit on the auth loader
 * indefinitely, and the WebView has no escape affordance.
 */
export const FLIGHT_ACTION_DECISION_TIMEOUT_MS = 10_000;

/** Entry-contract action (lowercase) → the interface's `FlightAction`. */
const FLIGHT_ACTION_BY_ENTRY_ACTION: Record<
  FlightExerciseAction,
  FlightAction
> = {
  cancel: FlightAction.Cancel,
  cfar: FlightAction.Cfar,
  dafar: FlightAction.Dafar,
};

/**
 * A settled routing decision. `identifier` is required on the commerce
 * branch (and absent elsewhere) so the URL builder can never be handed an
 * `undefined` order id; `reason` is the telemetry attribution.
 */
type RoutingDecision =
  | { outcome: "commerce"; reason: string; identifier: string }
  | { outcome: "legacy"; reason: string }
  | {
      outcome: "trip_details";
      reason: string;
      state?: FlightActionState;
      failureReason?: string;
    };

/**
 * Routes a native flight post-booking action entry
 * (`/trips/actions/{orderId}/{action}` via the startsession
 * `redirect_uri`) to the commerce portal, the legacy b2b flow, or the
 * trip's details page.
 *
 * Pass `null` while there is no pending action entry; the hook is
 * inert. Entries are provided only after `startSession` succeeds, and
 * the fetch below needs the session cookie, so authentication is
 * complete before any of this runs.
 *
 * The BFF's `actionCapabilities` (read from the My Trips list) are the
 * ONLY routing source, for every user: the destination is a property of
 * the order, never of the user cohort, so no rollout flag is consulted
 * here, the same principle as `useCommerceFlightActionHandoff`. The
 * kill switch is the BFF itself: when it sends no capabilities, the
 * entry falls back to the pre-capability legacy path.
 *
 * - `Available` + `Commerce` → full-page `window.location.replace` to
 *   the commerce action route, using the CAPABILITY's identifier (the
 *   commerce order id), not the entry's. Same-host; istio routes
 *   `/air/trips/*` to commerce-portals, and an SPA navigate would be
 *   swallowed by the b2bportal catch-all. Held until the experiments
 *   fetch settles (bounded by the backstop) so the forwarded hand-back
 *   bit on the URL is not committed as a stale false.
 * - `Available` + `Legacy` → the legacy b2b flow, as before. The
 *   navigation carries a location-state marker so the trip-details
 *   commerce interception stands down for cancel/CFAR (legacy DAFAR
 *   has its own route and is never intercepted).
 * - No capabilities on the itinerary → the legacy b2b flow: the BFF
 *   does not compute capabilities (tenant-level kill switch, or a BFF
 *   that predates the field), so the pre-existing path is the only
 *   safe answer. This mirrors the details screen, whose CTAs also
 *   revert to their pre-capability chains on `not-computed`.
 * - Anything else (`Unavailable`, `Unresolved`, no capability entry
 *   for the action, the order missing from the list, the list fetch
 *   failing or hanging) → land on the trip's details
 *   (`/trips/?tripId=…`) rather than a dead-ended or guessed action;
 *   details is where contact-support lives, and its own CTAs are
 *   capability-gated. Which details surface renders is the
 *   trip-details interception's decision (commerce for its rollout
 *   cohort, legacy otherwise); both are that user's working support
 *   surface.
 *
 * The LIST endpoint is deliberate: on the single-itinerary endpoint the
 * BFF cannot resolve the itinerary's time bucket, so cancel/cfar come
 * back `Unresolved(itinerary_state_unknown)` by design; the upstream
 * fix is tracked separately.
 *
 * `VITE_FORCE_COMMERCE_FLIGHTS_MYTRIPS_REDIRECT` forces the commerce
 * branch in local dev without a capability read; it routes on the
 * entry's own orderId. That force-flag semantic is shared with
 * `FlightSearchRoute` and `useCommerceFlightTripDetailsRedirect`.
 */
export function useFlightActionEntryRedirect(
  entry: FlightActionEntry | null
): void {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const forceCommerceRedirect =
    import.meta.env.VITE_FORCE_COMMERCE_FLIGHTS_MYTRIPS_REDIRECT === "true";

  // Forwarded eligibility bit for commerce hand-offs (commerce adjusts
  // its hand-back behavior to whether the native trips list exists),
  // never a routing gate. `useExperiment` reads false until the provider
  // settles, so a commerce redirect, which is a one-shot full page load,
  // is held until `isFetched` (bounded by the backstop below) rather
  // than committed with a stale false. Legacy and details settles carry
  // no experiment data and never wait.
  const externalTripsListEnabled = useExperiment(
    FEATURE_FLAGS.EXTERNAL_TRIPS_LIST
  );
  const { isFetched = false } = useExperiments();

  // The redirect must fire exactly once per entry: providers can
  // re-render the tree after the decision (e.g. an experiments refresh
  // nonce), and re-running a location.replace / navigate would fight the
  // navigation already in flight. Keyed on the entry's identity, not
  // a plain boolean, so a subsequent *different* entry still routes.
  const handledEntryKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!entry) {
      return;
    }
    const entryKey = `${entry.orderId}/${entry.action}`;
    if (handledEntryKeyRef.current === entryKey) {
      return;
    }

    let timeoutId: number | undefined;
    let cancelled = false;

    const settle = (decision: RoutingDecision) => {
      // The backstop and the list fetch race each other; whichever
      // arrives first owns the navigation, and `cancelled` keeps an
      // unmounted component from navigating on a late response.
      if (cancelled || handledEntryKeyRef.current === entryKey) {
        return;
      }
      handledEntryKeyRef.current = entryKey;
      window.clearTimeout(timeoutId);

      // Launch telemetry: distinguishes portal routing decisions from
      // entry/auth failures when debugging the native hand-off, and
      // attributes every non-action landing to a reason code. No user
      // identifiers: `orderId` and `identifier` are booking ids.
      logger().info("NU-609: flight action entry routed", {
        action: entry.action,
        orderId: entry.orderId,
        ...decision,
      });

      switch (decision.outcome) {
        case "commerce":
          window.location.replace(
            buildCommerceFlightActionUrl(
              decision.identifier,
              entry.action,
              externalTripsListEnabled === true
            )
          );
          break;
        case "legacy":
          navigate(buildLegacyFlightActionPath(entry), {
            replace: true,
            // The legacy cancel/CFAR flow lives behind the `?tripId=`
            // details screen, whose commerce interception would otherwise
            // re-route this action to commerce; the marker tells it to
            // stand down (see useCommerceFlightTripDetailsRedirect).
            state: {
              legacyFlightAction: true,
            } satisfies FlightTripDetailsLocationState,
          });
          break;
        case "trip_details":
          navigate(
            `${PATH_TRIPS}?tripId=${encodeURIComponent(entry.orderId)}`,
            { replace: true }
          );
          break;
      }
    };

    // Local dev override: skip the capability read and hand off on the
    // entry's own orderId, as before.
    if (forceCommerceRedirect) {
      settle({
        outcome: "commerce",
        reason: "force_override",
        identifier: entry.orderId,
      });
      return;
    }

    // A commerce answer that arrived before the experiments settled: the
    // effect re-runs when `isFetched` flips (RTK Query answers the
    // re-dispatch from cache) and commits with the settled bit. If the
    // experiments hang instead, the backstop commits it with the bit
    // as-is: a stale hand-back bit beats stranding the action.
    let heldCommerce: RoutingDecision | null = null;

    // Stranding backstop. A hung lookup is a failed capability
    // resolution, not a licence to guess an action destination: a
    // commerce-only order has no legacy flow to enter, so land on the
    // trip's details like every other unresolved answer.
    timeoutId = window.setTimeout(() => {
      logger().warn("NU-609: flight action entry decision timed out", {
        action: entry.action,
        orderId: entry.orderId,
        timeoutMs: FLIGHT_ACTION_DECISION_TIMEOUT_MS,
      });
      settle(
        heldCommerce ?? {
          outcome: "trip_details",
          reason: "capability_timeout",
        }
      );
    }, FLIGHT_ACTION_DECISION_TIMEOUT_MS);

    const query = dispatch(
      flightsPostBookingApi.endpoints.fetchAllFlightItineraries.initiate({
        states: ALL_FLIGHT_STATES,
        referenceDateTime: dayjs().format(),
      })
    );

    query
      .unwrap()
      .then((response) => {
        const itinerary = Object.values(response?.itineraries ?? {})
          .flat()
          .find(
            (candidate) => candidate?.bookedItinerary?.id === entry.orderId
          );

        if (!itinerary) {
          settle({ outcome: "trip_details", reason: "itinerary_not_found" });
          return;
        }

        const route = resolveFlightActionRoute(
          itinerary,
          FLIGHT_ACTION_BY_ENTRY_ACTION[entry.action]
        );

        switch (route.kind) {
          case "commerce":
            if (!isFetched) {
              heldCommerce = {
                outcome: "commerce",
                reason: "capability_commerce",
                identifier: route.identifier,
              };
              break;
            }
            settle({
              outcome: "commerce",
              reason: "capability_commerce",
              identifier: route.identifier,
            });
            break;
          case "legacy":
            settle({ outcome: "legacy", reason: "capability_legacy" });
            break;
          case "unavailable":
            settle({
              outcome: "trip_details",
              reason: "capability_unavailable",
              state: route.state,
              failureReason: route.failureReason,
            });
            break;
          case "not-computed":
            settle({ outcome: "legacy", reason: "capabilities_not_computed" });
            break;
        }
      })
      .catch(() => {
        // The list fetch is the capability source; without it there is no
        // trustworthy action target, so land on details rather than guess.
        settle({ outcome: "trip_details", reason: "list_fetch_failed" });
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      query.unsubscribe();
    };
  }, [
    dispatch,
    entry,
    externalTripsListEnabled,
    forceCommerceRedirect,
    isFetched,
    navigate,
  ]);
}
